<template>
  <div class="maintenance-create">
    <AppHeader
      title="Schedule Maintenance"
      description="Create a new maintenance record for equipment"
      :back-link="{ name: 'Maintenance' }"
    />

    <div class="max-w-4xl mx-auto">
      <div v-if="serverErrors && serverErrors.length" class="mb-4">
        <div class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <strong class="block font-semibold">Server validation errors:</strong>
          <ul class="mt-2 list-disc list-inside text-sm">
            <li v-for="(err, idx) in serverErrors" :key="idx">{{ err }}</li>
          </ul>
        </div>
      </div>
      <!-- Progress Steps -->
      <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div class="flex justify-between items-center mb-8">
          <div v-for="(step, index) in steps" :key="step" class="flex items-center">
            <div class="flex items-center">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center border-2"
                :class="
                  currentStep >= index
                    ? 'border-blue-500 bg-blue-500 text-white'
                    : 'border-gray-300 text-gray-400'
                "
              >
                {{ index + 1 }}
              </div>
              <span
                class="ml-2 text-sm font-medium"
                :class="currentStep >= index ? 'text-blue-600' : 'text-gray-500'"
              >
                {{ step }}
              </span>
            </div>
            <div v-if="index < steps.length - 1" class="w-12 h-0.5 bg-gray-300 mx-4"></div>
          </div>
        </div>

        <!-- Step 1: Equipment Selection -->
        <div v-if="currentStep === 0" class="space-y-6">
          <h3 class="text-lg font-semibold text-gray-800">Select Equipment</h3>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="equipment in equipmentList"
              :key="equipment.id"
              class="border rounded-lg p-4 cursor-pointer transition-all duration-200"
              :class="
                selectedEquipment?.id === equipment.id
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50'
              "
              @click="selectEquipment(equipment)"
            >
              <div class="flex items-center justify-between mb-2">
                <h4 class="font-medium text-gray-900">{{ equipment.name }}</h4>
                <span
                  class="text-sm px-2 py-1 rounded-full"
                  :class="
                    equipment.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  "
                >
                  {{ equipment.status }}
                </span>
              </div>
              <p class="text-sm text-gray-600">{{ equipment.type }} • {{ equipment.model }}</p>
              <p class="text-xs text-gray-500 mt-2">Serial: {{ equipment.serial_number }}</p>

              <div v-if="equipment.last_maintenance_date" class="mt-2 text-xs text-gray-500">
                Last maintenance: {{ formatDate(equipment.last_maintenance_date) }}
              </div>
            </div>
          </div>

          <div class="flex justify-end pt-6">
            <button @click="nextStep" class="btn-primary" :disabled="!selectedEquipment">
              Next: Maintenance Details
            </button>
          </div>
        </div>

        <!-- Step 2: Maintenance Details -->
        <div v-else-if="currentStep === 1" class="space-y-6">
          <h3 class="text-lg font-semibold text-gray-800">Maintenance Details</h3>

          <MaintenanceForm
            :equipment="selectedEquipment"
            @save="handleSave"
            @cancel="handleCancel"
          />
        </div>

        <!-- Step 3: Confirmation -->
        <div v-else-if="currentStep === 2" class="text-center py-8">
          <div
            class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <span class="text-2xl">✅</span>
          </div>
          <h3 class="text-lg font-semibold text-gray-800 mb-2">Maintenance Scheduled!</h3>
          <p class="text-gray-600 mb-6">The maintenance has been successfully scheduled.</p>

          <div class="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h4 class="font-medium mb-2">Scheduled Maintenance Details:</h4>
            <p><strong>Equipment:</strong> {{ savedMaintenance?.equipment?.name }}</p>
            <p><strong>Type:</strong> {{ savedMaintenance?.maintenance_type }}</p>
            <p><strong>Date:</strong> {{ formatDate(savedMaintenance?.maintenance_date || '') }}</p>
            <p><strong>Description:</strong> {{ savedMaintenance?.description }}</p>
          </div>

          <div class="flex justify-center space-x-4">
            <RouterLink to="/maintenance" class="btn-secondary">View All Maintenance</RouterLink>
            <button @click="scheduleAnother" class="btn-primary">Schedule Another</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMaintenanceStore } from '@/stores/maintenanceStore'
import { useEquipmentStore } from '@/stores/equipmentStore'
import type { Maintenance, MaintenanceCreate } from '@/types/maintenance'
import type { Equipment } from '@/types/equipment'
import AppHeader from '@/components/shared/AppHeader.vue'
import MaintenanceForm from '@/components/maintenance/MaintenanceForm.vue'
import { useNotificationStore } from '@/stores/notification'

const router = useRouter()
const maintenanceStore = useMaintenanceStore()
const equipmentStore = useEquipmentStore()
const notificationStore = useNotificationStore()

const currentStep = ref(0)
const selectedEquipment = ref<Equipment | null>(null)
const savedMaintenance = ref<Maintenance | null>(null)
const serverErrors = ref<string[]>([])

const steps = ['Equipment', 'Details', 'Confirmation']

const equipmentList = computed(() =>
  equipmentStore.equipment.filter((eq) => eq.status === 'active'),
)

onMounted(() => {
  equipmentStore.fetchEquipment()
})

const selectEquipment = (equipment: Equipment) => {
  selectedEquipment.value = equipment
}

const nextStep = () => {
  if (currentStep.value < steps.length - 1) {
    currentStep.value++
  }
}

const handleSave = async (data: MaintenanceCreate) => {
  try {
    serverErrors.value = []
    const maintenance = await maintenanceStore.createMaintenance(data)
    // Ensure confirmation has equipment details for display
    if (!maintenance.equipment && selectedEquipment.value) {
      ;(maintenance as Maintenance).equipment = selectedEquipment.value
    }
    savedMaintenance.value = maintenance
    currentStep.value = 2
  } catch (error) {
    console.error('Failed to create maintenance:', error)
    // Try to surface backend validation errors
    const maybeErr = error as unknown
    if (typeof maybeErr === 'object' && maybeErr !== null && 'response' in maybeErr) {
      const resp = (maybeErr as { response?: { data?: unknown; status?: number } }).response
      const info = resp?.data
      const status = resp?.status

      if (status === 401) {
        notificationStore.showError('Session expired or unauthorized. Please log in again.')
        router.push('/login')
        return
      }

      if (info) {
        console.error('Backend response data:', info)
        // If backend provides a details array, show its contents clearly
        if (typeof info === 'object' && info !== null) {
          const be = info as Record<string, unknown>
          const maybeDetails = be['details']
          if (Array.isArray(maybeDetails) && maybeDetails.length > 0) {
            const lines = (maybeDetails as unknown[]).map((d) =>
              typeof d === 'string' ? d : JSON.stringify(d),
            )
            serverErrors.value = lines
            notificationStore.showError(lines.join('\n'))
            return
          }
        }

        const pretty = typeof info === 'object' ? JSON.stringify(info, null, 2) : String(info)
        notificationStore.showError(`Failed to create maintenance (server validation error):\n${pretty}`)
        return
      }
    }

    notificationStore.showError('Failed to create maintenance. Check console for details.')
  }
}

const handleCancel = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  } else {
    router.push('/maintenance')
  }
}

const scheduleAnother = () => {
  currentStep.value = 0
  selectedEquipment.value = null
  savedMaintenance.value = null
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}
</script>
